import { Workflow, FolderKanban, BadgeCheck, Target, Quote, Phone, Mail } from "lucide-react";

const SERVICES = [
  [Workflow, "Business Process Reengineering & Automation", "Enhancing and optimising business procedures, then automating them for speed and consistency."],
  [FolderKanban, "PMO Design & Deployment", "Helping businesses establish and run effective Project Management Offices."],
  [BadgeCheck, "ISO 9000 Advisory Services", "Expert guidance through the ISO 9000 certification process to improve quality management."],
  [Target, "Quality Programme Design & Deployment", "Raising the quality of products, services and processes across the business."],
];
const STEPS = [
  ["BPRR Phase One", "Diagnosis and optimisation: problem identification, architecture development and solution blueprinting."],
  ["Reporting", "A detailed report with synthesis and thorough analysis of the benefits to be accrued."],
  ["BPRR Phase Two", "Solutions are deployed and tested with interventions that sustain the gains long term."],
];
const CASES = [["Mining", "Reducing Pit Wall Instability"], ["Manufacturing", "Reducing Incidents of Jerrycan Failures"], ["Finance", "Reduction of Delays in Business Cheque Payments"]];
const TEAM = [["Daniel Adjepong", "Managing Consultant", 1], ["James Awumee", "Expert Consultant", 2], ["Richard Amewuga", "Business Consulting", 3], ["Dr. Benonia Aryee", "Soft Skill Consultant", 4]];
const QUOTES = [
  ["Dexterity supported five Greenbelt projects in quality, operational excellence, and innovation within six months, achieving over 90% success rates. Their strong coaching and advisory support boosted Edikan's Lean Six Sigma journey.", "David Agyemang", "HR Manager, Edikan", "edikan"],
  ["Dexterity Consult has been on the forefront of delivering real-time capacity to organizations in Ghana and abroad through practical training and hands-on application of business improvement methodologies.", "Mark Badu-Aboagye", "CEO, Ghana Chamber of Commerce and Industry", "gcc"],
  ["I needed new direction; I needed new focus for the team. Dexterity Consult partnered with me to bring focus and dynamism to the team. We still use most of the tools in their everyday work.", "Stephen Essien", "Head, Customer Operations, Tigo Ghana", "tigo"],
  ["Fidelity Bank hired Dexterity for Lean Six Sigma to cut costs, eliminate waste, and find revenue opportunities. The program successfully identified urgent issues and stressed collaboration with stakeholders.", "Simon Adu-Gyamfi", "Director, Banking Operations & Optimization, Fidelity Bank Ghana", "fidelity"],
  ["Dexterity Consult empowered me with tools and techniques for process improvement and Total Quality Management. Their deep dive into 5S methodology transformed my team's work and significantly improved factory safety.", "Ing. Richard Osei-Mensah", "Head of Engineering, Thai Union Group", "thai"],
];
const STATS = [["100+", "years of combined working experience"], ["98%+", "successful projects"], ["4.7", "average customer rating"]];
const Eyebrow = ({ children }) => <div className="flex items-center gap-3"><span className="h-px w-10 bg-[#D4AF37]" /><span className="dx-eyebrow">{children}</span></div>;

export const ConsultServices = () => (
  <section className="max-w-7xl mx-auto px-5 sm:px-10 py-20 lg:py-28" data-testid="consult-services">
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <Eyebrow>Dexterity Consult</Eyebrow>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 text-[#250E31]">Build operational excellence</h2>
        <p className="text-[#6b5a73] mt-4">More business, highest quality, at less cost and fastest delivery. Beyond recruitment and training, our consultants help organisations redesign processes, deploy quality programmes and achieve ISO certification.</p>
        <div className="grid grid-cols-3 gap-4 mt-8">
          {STATS.map(([v, l]) => <div key={l} className="border-l-2 border-[#D4AF37] pl-3"><div className="font-display text-3xl font-extrabold text-[#5B2A6E]">{v}</div><div className="text-xs text-[#6b5a73] mt-1">{l}</div></div>)}
        </div>
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
        {SERVICES.map(([Icon, t, d]) => (
          <div key={t} className="dx-card p-6" data-testid="consult-service">
            <div className="w-10 h-10 rounded-md bg-[#5B2A6E] grid place-items-center"><Icon className="w-5 h-5 text-[#D4AF37]" /></div>
            <h3 className="font-display font-bold mt-4 text-[#250E31]">{t}</h3>
            <p className="text-sm text-[#6b5a73] mt-2">{d}</p>
          </div>
        ))}
      </div>
    </div>
    <div className="mt-16 grid md:grid-cols-3 gap-0 border border-[rgba(91,42,110,.14)]">
      {STEPS.map(([t, d], i) => (
        <div key={t} className="p-6 border-b md:border-b-0 md:border-r last:border-0">
          <div className="text-xs font-bold text-[#D4AF37] uppercase tracking-[0.2em]">Step {i + 1}</div>
          <h3 className="font-display font-bold text-lg mt-2 text-[#250E31]">{t}</h3>
          <p className="text-sm text-[#6b5a73] mt-2">{d}</p>
        </div>
      ))}
    </div>
    <p className="text-xs text-[#8a7a92] mt-3">The BPRR framework deploys Six Sigma DMAIC for breakthrough process performance.</p>
  </section>
);

export const CasesTeam = () => (
  <section className="bg-[#FAF8FC] border-y" data-testid="consult-cases-team">
    <div className="max-w-7xl mx-auto px-5 sm:px-10 py-20 grid lg:grid-cols-2 gap-14">
      <div>
        <Eyebrow>Case studies</Eyebrow>
        <h2 className="text-3xl font-bold tracking-tight mt-3 text-[#250E31]">Results across industries</h2>
        <div className="mt-8 space-y-3">
          {CASES.map(([sector, t]) => (
            <div key={t} className="dx-card p-5 flex items-center gap-4" data-testid="consult-case">
              <span className="text-[11px] font-extrabold uppercase tracking-wider bg-[#5B2A6E] text-white px-2.5 py-1">{sector}</span>
              <span className="font-semibold text-[#250E31]">{t}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <Eyebrow>Professional team</Eyebrow>
        <h2 className="text-3xl font-bold tracking-tight mt-3 text-[#250E31]">Our consultants</h2>
        <div className="mt-8 grid grid-cols-2 gap-3">
          {TEAM.map(([n, r, i]) => (
            <div key={n} className="dx-card overflow-hidden group" data-testid="consult-team-member">
              <div className="aspect-[298/267] overflow-hidden bg-[#F4EEF7]">
                <img src={`/brand/team/team-${i}.jpg`} alt={n} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" data-testid={`team-photo-${i}`} />
              </div>
              <div className="p-4 border-t-2 border-[#D4AF37]">
                <div className="font-semibold text-[#250E31]">{n}</div>
                <div className="text-xs text-[#6b5a73]">{r}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const Testimonials = () => (
  <section className="max-w-7xl mx-auto px-5 sm:px-10 py-20 lg:py-28" data-testid="testimonials">
    <Eyebrow>Testimonials</Eyebrow>
    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 text-[#250E31]">What people say about us</h2>
    <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      {QUOTES.map(([q, n, r, logo]) => (
        <figure key={n} className="dx-card p-6 flex flex-col" data-testid="testimonial">
          <Quote className="w-6 h-6 text-[#D4AF37]" />
          <blockquote className="text-sm text-[#3b2f42] mt-3 flex-1 leading-relaxed">{q}</blockquote>
          <figcaption className="mt-5 border-t pt-4 flex items-center gap-3">
            <img src={`/brand/clients/${logo}.png`} alt={r} loading="lazy" className="w-11 h-11 rounded-full object-cover border border-[#D4AF37]/50 shrink-0" data-testid={`testimonial-photo-${logo}`} />
            <div><div className="font-semibold text-[#250E31]">{n}</div><div className="text-xs text-[#6b5a73]">{r}</div></div>
          </figcaption>
        </figure>
      ))}
    </div>
  </section>
);

export const ConsultContact = () => (
  <div className="space-y-2 text-sm text-white/75">
    <div className="flex gap-2"><Phone className="w-4 h-4 text-[#D4AF37]" />+233 30 295 2799</div>
    <div className="flex gap-2"><Mail className="w-4 h-4 text-[#D4AF37]" />enquiry@dexterityconsult.co</div>
  </div>
);
